import React, { useState, useEffect, useContext } from "react";
import { Link } from "react-router-dom";
import { useRolePermissions } from "../Settings/RoleContext";
import api from "../../api/axiosConfig";
import AddUser from "../DialBox/AddUser";
import "../../styles/Settings/Settings.css";
import { ProjectContext } from "../../App";
import { useAuth } from "../Authentication/AuthContext";
import UploadUsersCsv from "./UploadUsersCsv";
import {
  MdKeyboardDoubleArrowLeft,
  MdKeyboardArrowLeft,
  MdKeyboardDoubleArrowRight,
  MdKeyboardArrowRight,
} from "react-icons/md";
import socket from "../../socket";
import { Pagination } from "@mui/material";

export const UserSettings = () => {
  const { isNavBarOpen } = useContext(ProjectContext);
  const [users, setUsers] = useState([]);
  const [filterByActive, setFilterByActive] = useState([]);
  const [roles, setRoles] = useState([]);
  const [userRoles, setUserRoles] = useState({});
  const { hasPermission } = useRolePermissions();
  const { openDialog, setOpenDialog } = useContext(ProjectContext);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { userDetails } = useAuth();
  const [sortConfig, setSortConfig] = useState({
    key: "",
    direction: "asc",
  });
  const userRole = userDetails.role;
  const [filter, setFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage, setUsersPerPage] = useState(25);
  const [activeUserLink, setUserActiveLink] = useState("activeusers");
  const [showUpload, setShowUpload] = useState(false);
  useEffect(() => {
    socket.on("newuser added", (newUser) => {
      setFilterByActive((prevUsers) => [...prevUsers, newUser]);
    });

    return () => {
      socket.off("newuser added");
    };
  }, []);

  const fetchData = async (url, setData, setError, setLoading) => {
    try {
      const response = await api.get(url);
      setData(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      setError(error);
    }
  };

  const updateStatus = async (newStatus, userId) => {
    try {
      await api.put(`/api/settings/users/${userId}/status`, {
        status: newStatus,
      });
      // Refresh data
      fetchAllData();
    } catch (error) {
      console.error("Failed to update status", error);
    }
  };

  const handleRoleChange = async (userId, e) => {
    const newRole = e.target.value;
    setUserRoles((prevRoles) => ({
      ...prevRoles,
      [userId]: newRole,
    }));
    try {
      const response = await api.put("/api/settings/role", {
        user_id: userId,
        role_name: newRole,
      });

      if (response.status === 200) {
        console.log("Role updated successfully");
      } else {
        console.error("Error updating role:", response.data.error);
      }
    } catch (error) {
      console.error("Request failed:", error.response?.data || error.message);
    }
  };

  const fetchAllData = async () => {
    const endpoint = `/api/settings/users`;
    fetchData(endpoint, setUsers, setError, setLoading);
    const roleEndpoint = "/api/roles";
    fetchData(roleEndpoint, setRoles, setError, setLoading);
  };
  useEffect(() => {
    fetchAllData();
  }, [userRole, userDetails.id, activeUserLink]);

  useEffect(() => {
    if (Array.isArray(users)) {
      const roles = users.reduce((acc, user) => {
        acc[user.id] = user.role_name; // Assuming role_name is available
        return acc;
      }, {});
      setUserRoles(roles);
    } else if (users.role_name) {
      setUserRoles({ [users.id]: users.role_name }); // Single user
    }
  }, [users]);

  const mapKeyToUserProperty = (heading) => {
    switch (heading) {
      case "User Name":
        return "name";
      case "Email ID":
        return "email";
      case "Role":
        return "role_name";
      case "Sort Code":
        return "sortcode";
      case "User ID":
        return "employeecode";
      case "Active Status":
        return "status";
      case "Assoc Grps":
        return "groups";
      case "Assoc Proj":
        return "projects";
      case "Hour Rate":
        return "hourrate";
      default:
        return "";
    }
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  useEffect(() => {
    if (!Array.isArray(users)) {
      setFilterByActive([]); // Ensure it's an array
      return;
    }
    if (activeUserLink === "activeusers") {
      setFilterByActive(users.filter((user) => user.status === "Active"));
    } else if (activeUserLink === "inactiveusers") {
      setFilterByActive(users.filter((user) => user.status === "Inactive"));
    } else {
      setFilterByActive(users);
    }
  }, [activeUserLink, users]);
  const sortedUsers = [...filterByActive].sort((a, b) => {
    if (sortConfig.key === null) return 0;
    const key = sortConfig.key || "name";
    const direction = sortConfig.direction === "desc" ? -1 : 1;

    // Sorting for "Associated Groups" and "Associated Projects" by length
    if (key === "groups" || key === "projects") {
      return (a[key].length - b[key].length) * direction;
    }

    // Sorting for "employeecode" (extracting numeric part)
    if (key === "employeecode") {
      const matchA = a[key]?.match(/^[A-Za-z]+/);
      const matchB = b[key]?.match(/^[A-Za-z]+/);

      const prefixA = matchA ? matchA[0] : "";
      const prefixB = matchB ? matchB[0] : "";

      if (prefixA !== prefixB) {
        return prefixA.localeCompare(prefixB) * direction;
      }

      const numA = parseInt(a[key]?.replace(/\D/g, ""), 10) || 0;
      const numB = parseInt(b[key]?.replace(/\D/g, ""), 10) || 0;

      return (numA - numB) * direction;
    }

    // Default string sorting for other columns
    if (a[key] < b[key]) return -1 * direction;
    if (a[key] > b[key]) return 1 * direction;
    return 0;
  });

  const filteredUsers = (Array.isArray(sortedUsers) ? sortedUsers : []).filter(
    (user) =>
      user.name?.toLowerCase().includes(filter) ||
      user.employeecode?.toLowerCase().includes(filter)
  );

  const handleSearch = (e) => {
    setFilter(e.target.value.toLowerCase());
  };

  // const finalUsers = sortedUsers;
  const totalPages = Math.ceil(filterByActive.length / usersPerPage);

  // Get current projects to display
  const indexOfLastUsers = currentPage * usersPerPage;
  const indexOfFirstUsers = indexOfLastUsers - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUsers, indexOfLastUsers);
  const calculatePaginationCount = (projectsPerPage, currentPage) => {
    const start = (currentPage - 1) * projectsPerPage + 1;
    const end = start + projectsPerPage - 1;
    return `${start}-${end}`;
  };
  const paginationCount = calculatePaginationCount(usersPerPage, currentPage);

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage((prev) => prev + 1);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) setCurrentPage((prev) => prev - 1);
  };
  const handleFirstPage = () => {
    if (currentPage > 1) setCurrentPage(1);
  };
  const handleLastPage = () => {
    if (currentPage < totalPages) setCurrentPage(totalPages);
  };
  const handleHourRateChange = (userId, newHourRate) => {
    // Update the state for the user hourrate
    setUsers((prevUsers) =>
      prevUsers.map((user) =>
        user.id === userId ? { ...user, hourrate: newHourRate } : user
      )
    );
  };

  const handleSortCodeChange = (userId, sortcode) => {
    // Update the state for the user hourrate
    setUsers((prevUsers) =>
      prevUsers.map((user) =>
        user.id === userId ? { ...user, sortcode: sortcode } : user
      )
    );
  };

  const updateHourRate = async (userId, value, field) => {
    try {
      // API call to update hourrate in the backend
      await api.put(`/api/users/${userId}/hourrate`, { value, field });
    } catch (error) {
      console.error("Failed to update hourrate", error);
    }
  };

  const handleUserLinkClick = (link) => {
    setUserActiveLink(link); // Update the active link
    setCurrentPage(1);
  };

  const handleUpdatePassword = async (userID) => {
    try {
      const response = await api.put(`/api/users/${userID}/password`, {
        isAdminReset: true, // Admin-triggered reset
      });

      if (response.status === 200) {
        alert(
          "Password reset successfully. Inform the user to change it after logging in."
        );
      } else {
        throw new Error(response.data.message || "Error resetting password");
      }
    } catch (error) {
      console.error("Error resetting password:", error);
      alert("Failed to reset the password. Please try again.");
    }
  };

  if (loading)
    return (
      <div
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
        }}
      >
        <span className="loading-animation">LOADING...</span>
      </div>
    );
  if (error) return;
  <div>Error loading data! {error}</div>;
  return (
    <div className="settings-container">
      {hasPermission("users", "create") && (
        <div className="add-user-container">
          <button
            className="upload-user-btn"
            onClick={() => setShowUpload(true)}
          >
            Upload Users
          </button>
          <a
            href="/sample_users_import.csv"
            download
            className="p-0  text-[14px] border-none rounded-[4px] cursor-pointer"
            style={{ textDecoration: "none" }}
          >
            Download Sample CSV
          </a>
          <button
            onClick={() => setOpenDialog((prev) => !prev)}
            className="add-user-settings-btn add-btn"
            title="Add User"
          >
            Add User
          </button>
        </div>
      )}
      {openDialog && <AddUser />}
      <div
        className={
          isNavBarOpen
            ? "user-settings-tab-details"
            : "user-settings-tab-details-close"
        }
      >
        <Link
          style={{ marginLeft: "15px" }}
          className={`proj-links ${
            activeUserLink === "activeusers" ? "active" : ""
          }`}
          onClick={() => handleUserLinkClick("activeusers")}
        >
          Active Users
        </Link>
        <Link
          className={`proj-links ${
            activeUserLink === "inactiveusers" ? "active" : ""
          }`}
          onClick={() => handleUserLinkClick("inactiveusers")}
        >
          Inactive Users
        </Link>
        <Link
          className={`proj-links ${
            activeUserLink === "overallusers" ? "active" : ""
          }`}
          onClick={() => handleUserLinkClick("overallusers")}
        >
          Overall Users
        </Link>
      </div>
      <input
        className="user-search-input"
        type="search"
        placeholder="Search by Name or ID"
        onChange={handleSearch}
      />{" "}
      {showUpload && <UploadUsersCsv setShowUpload={setShowUpload} />}
      <div className="user-table-wrapper">
        <table
          className={
            isNavBarOpen
              ? "settings-content-table"
              : "settings-content-table-close"
          }
        >
          <thead style={{ position: "sticky", top: "-1px" }}>
            <tr>
              {[
                "User ID",
                "User Name",
                "Sort Code",
                "Email ID",
                "Active Status",
                "Assoc Grps",
                "Assoc Proj",
                "Role",
                "Hour Rate",
                "Action",
                "",
              ].map((heading, index) => (
                <th
                  key={index}
                  onClick={
                    heading !== "Action" || heading !== ""
                      ? () => handleSort(mapKeyToUserProperty(heading))
                      : null
                  }
                  style={{
                    maxWidth: heading === "S.No" ? "10px" : "auto",
                    textAlign:
                      heading === "Assoc Grps" || heading === "Assoc Proj"
                        ? "center"
                        : "",
                    ...(heading === "User ID" && {
                      minWidth: "45px",
                    }),
                  }}
                >
                  {heading}
                  {heading !== "Assoc Grps" &&
                    heading !== "Assoc Proj" &&
                    heading !== "Action" &&
                    heading !== "" &&
                    sortConfig.key === mapKeyToUserProperty(heading) && (
                      <span>
                        {sortConfig.direction === "asc" ? " ↑" : " ↓"}
                      </span>
                    )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {currentUsers.map((user) => (
              <tr key={user.id}>
                <td>{user.employeecode}</td>
                <td>
                  <Link
                    style={{
                      display: "flex",
                      textDecoration: "none",
                      color: "black",
                      alignItems: "center",
                    }}
                    to={`/user-details/${user.id}`}
                  >
                    {user.picture && user.picture !== "null" ? (
                      <img
                        className="user-dp"
                        src={
                          user.picture
                            ? `${api.defaults.baseURL}${user.picture}`
                            : "/default-avatar.png"
                        }
                        alt="profile"
                      />
                    ) : (
                      <span className="user-initial">
                        {user.name.charAt(user.name.length - 1).toUpperCase() +
                          user.name.charAt(0).toUpperCase()}
                      </span>
                    )}
                    <p className="users-name"> {user.name}</p>
                  </Link>
                </td>
                <td style={{ padding: "6px" }}>
                  <input
                    type="text"
                    className="sort-code"
                    value={user.sortcode}
                    onChange={(e) =>
                      handleSortCodeChange(user.id, e.target.value)
                    }
                    onBlur={() =>
                      updateHourRate(user.id, user.sortcode, "sortcode")
                    }
                    disabled={!hasPermission("users", "update")}
                    maxLength={3}
                  />
                </td>
                <td>{user.email}</td>
                <td
                  style={{
                    color: "white",
                    width: "140px",
                    minWidth: "140px",
                    textAlign: "center",
                  }}
                >
                  <select
                    className="status-dropdown-user"
                    value={user.status}
                    onChange={(e) => updateStatus(e.target.value, user.id)}
                    style={{
                      border: "none",
                      backgroundColor:
                        user.status === "Active"
                          ? "rgb(122, 206, 76)"
                          : "#d2b48c",
                      color: "white",
                      paddingLeft: "38px",
                      paddingRight: "28px",
                      cursor: "pointer",
                    }}
                    disabled={
                      !hasPermission("users", "delete") ||
                      user.role_name === "Super Admin"
                    }
                  >
                    <option
                      style={{
                        backgroundColor: "white",
                        color: "black",
                      }}
                      value="Inactive"
                    >
                      Inactive
                    </option>
                    <option
                      style={{
                        backgroundColor: "white",
                        color: "black",
                      }}
                      value="Active"
                    >
                      Active
                    </option>
                  </select>
                </td>
                <td style={{ width: "5px", textAlign: "center" }}>
                  {user.groups.length}
                </td>
                <td style={{ width: "5px", textAlign: "center" }}>
                  {user.projects.length}
                </td>
                {/* <td>{user.role_name}</td> */}
                <td>
                  <select
                    value={userRoles[user.id] || user.role_name} // Use role for each user
                    onChange={(e) => handleRoleChange(user.id, e)}
                    disabled={
                      !hasPermission("users", "update") ||
                      user.role_name === "Super Admin"
                    }
                    className="role-input"
                    style={{ marginBottom: "0px" }}
                  >
                    {roles.map((role) => (
                      <option key={role.id} value={role.name}>
                        {role.name}
                      </option>
                    ))}
                  </select>
                </td>
                <td style={{ textAlign: "center" }}>
                  <input
                    type="text"
                    className="invoice-element"
                    placeholder="Enter hour rate"
                    value={
                      userDetails.role_name === "Super Admin"
                        ? user.hourrate
                        : "*".repeat(String(user.hourrate).length || 1) // fallback to one star
                    }
                    onChange={(e) =>
                      handleHourRateChange(user.id, e.target.value)
                    } // Handle input changes
                    onBlur={() =>
                      updateHourRate(user.id, user.hourrate, "hourrate")
                    } // Optional: Save on blur
                    disabled={!hasPermission("users", "update")}
                    style={{ width: "58px", textAlign: "center" }}
                  />
                </td>
                <td>
                  <button
                    className="reset-btn"
                    onClick={() => handleUpdatePassword(user.id)} // Corrected invocation
                  >
                    Reset Password
                  </button>
                </td>
                <td style={{ width: "360px" }}></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="user-pagination">
        <h3
          style={{
            backgroundColor: "transparent",
            fontWeight: "normal",
            fontSize: "14px",
            margin: "0",
          }}
        >
          Total : {filteredUsers.length}
        </h3>
        <select
          className="project-list-row-count"
          value={usersPerPage}
          onChange={(e) => {
            setUsersPerPage(Number(e.target.value));
          }}
        >
          <option value="25">25 Records Per Page</option>
          <option value="50">50 Records Per Page</option>
          <option value="75">75 Records Per Page</option>
          <option value="100">100 Records Per Page</option>
        </select>
        <button
          onClick={handleFirstPage}
          disabled={currentPage === 1}
          className="arrow-project-btn"
        >
          <MdKeyboardDoubleArrowLeft
            title="First Page"
            style={{ backgroundColor: "transparent" }}
          />
        </button>
        <button
          onClick={handlePreviousPage}
          disabled={currentPage === 1}
          className="arrow-project-btn"
        >
          <MdKeyboardArrowLeft
            title="Previous Page"
            style={{ backgroundColor: "transparent" }}
          />
        </button>
        <span
          style={{
            marginTop: "2px",
            textWrap: "nowrap",
            backgroundColor: "transparent",
          }}
        >
          {paginationCount}
        </span>
        <button
          onClick={handleNextPage}
          disabled={currentPage === totalPages}
          className="arrow-project-btn"
        >
          <MdKeyboardArrowRight
            title="Next Page"
            style={{ backgroundColor: "transparent" }}
          />
        </button>
        <button
          onClick={handleLastPage}
          disabled={currentPage === totalPages}
          className="arrow-project-btn"
        >
          <MdKeyboardDoubleArrowRight
            title="Last Page"
            style={{ backgroundColor: "transparent" }}
          />
        </button>
      </div>
    </div>
  );
};
